import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HelpService } from '@service/help.service';
import { Context } from '@service/context.service';
import { marked } from 'marked';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { AbstractContentComponent } from '../abstract-content.component';
import { downloadBlob } from '../download';
import { isExternalLink, resolveHelpPath } from './help.feature';

/**
 * Shows one Markdown page of the help (`route.data.page`, e.g. `anwender/index.md`).
 * Relative links in the page are downloaded through the API, because the files need the access token.
 */
@Component({
  selector: 'app-help-page',
  imports: [ProgressSpinnerModule],
  templateUrl: './help-page.component.html',
  styleUrl: './help-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(click)': 'onContentClick($event)' },
})
export class HelpPageComponent extends AbstractContentComponent {
  private helpService = inject(HelpService);

  readonly html = signal('');
  readonly pageLoading = signal(true);
  private pagePath = '';

  constructor() {
    super(Context.Global);

    this.activatedRoute.data
      .pipe(
        map((data) => data['page'] as string),
        tap((page) => {
          this.pagePath = page;
          this.pageLoading.set(true);
        }),
        switchMap((page) =>
          this.helpService.getPage(page).pipe(
            catchError(() => {
              this.showErrorMessage(this.translate('HELP.PAGE_LOAD_FAILED'));
              return of('');
            }),
            finalize(() => this.pageLoading.set(false))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe((markdown) => this.html.set(marked.parse(markdown, { async: false })));
  }

  onContentClick = (event: MouseEvent): void => {
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href');
    if (!href || href.startsWith('#')) {
      return;
    }

    event.preventDefault();

    if (isExternalLink(href)) {
      window.open(href, '_blank', 'noopener');
      return;
    }

    const path = resolveHelpPath(this.pagePath, href);
    const filename = path.substring(path.lastIndexOf('/') + 1);

    this.showLoadingDialog();
    this.helpService
      .getFile(path)
      .pipe(finalize(() => this.hideLoadingDialog()))
      .subscribe({
        next: (blob) => downloadBlob(blob, filename),
        error: () => this.showErrorMessage(this.translate('HELP.DOWNLOAD_FAILED')),
      });
  };
}
